// Spanish translations of the 4 legal pages. Imported by each legal page
// when the user has selected Spanish via the language switcher.
import { ShieldCheck, Crown, Star, Lock, AlertTriangle, MessageCircle } from 'lucide-react';

const Pillar = ({ Icon, color, title, body }) => (
  <div className="bg-surface rounded-2xl p-6 border border-border flex flex-col">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="font-heading text-lg font-bold text-text-primary mb-2">{title}</h3>
    <p className="text-sm text-text-secondary leading-relaxed">{body}</p>
  </div>
);

export const TrustSafetyES = () => (
  <>
    <p>
      Creamos Match My Cleaner porque dejar entrar a un extraño en tu hogar nunca debería sentirse como un acto de fe.
      Cada limpiador que ves en esta plataforma ha pasado un proceso de verificación de varios pasos —
      y las insignias en su perfil te dicen exactamente hasta dónde han llegado.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose my-8">
      <Pillar Icon={ShieldCheck} color="bg-emerald-500" title="Antecedentes verificados"
        body="Cada limpiador pasa una verificación de antecedentes estándar de la industria a través de Checkr — cubriendo registros criminales nacionales, registros de delincuentes sexuales y listas de vigilancia globales. No mostramos limpiadores sin verificar en la búsqueda." />
      <Pillar Icon={Crown} color="bg-amber-500" title="Niveles Premium / Elite"
        body="Los limpiadores ganan el estatus Premium con 4.7+ de promedio en 10+ reseñas, y Elite con 4.9+ en 25+. Es basado en reputación, no en pagos." />
      <Pillar Icon={Star} color="bg-rose-500" title="Reseñas verificadas"
        body="Solo los clientes que completaron una reserva pagada pueden dejar una reseña — sin trolls anónimos, sin 5 estrellas incentivadas de amigos." />
      <Pillar Icon={Lock} color="bg-text-primary" title="Pagos seguros"
        body="Todos los pagos pasan por Stripe. Nunca almacenamos los datos de tu tarjeta, y a tu limpiador nunca se le paga hasta que confirmes el trabajo." />
    </div>

    <h2>Qué significa realmente "Antecedentes verificados"</h2>
    <p>
      Cuando un limpiador se registra, consiente una verificación de antecedentes realizada por <strong>Checkr</strong>,
      el mismo proveedor que usan Uber, Instacart y Lyft. La verificación cubre:
    </p>
    <ul>
      <li>Rastreo de SSN y verificación de identidad</li>
      <li>Búsqueda nacional de antecedentes criminales (delitos graves y menores, 7 años)</li>
      <li>Registro de delincuentes sexuales en los 50 estados</li>
      <li>Lista de vigilancia global (OFAC, FBI, Interpol)</li>
    </ul>
    <p>
      Solo los limpiadores con un reporte <strong>Clear</strong> pueden aparecer en los resultados de búsqueda o aceptar reservas.
      Los marcados como <em>Considerar</em> son pausados pendiente de revisión humana y un proceso formal de Acción Adversa —
      cumplimos con FCRA al pie de la letra.
    </p>

    <h2>El acuerdo del limpiador</h2>
    <p>Cada limpiador firma un Acuerdo de Plataforma vinculante al registrarse, comprometiéndose a:</p>
    <ul>
      <li>Nunca solicitar pago fuera de Match My Cleaner</li>
      <li>Nunca compartir información personal de contacto para eludir la plataforma</li>
      <li>Llegar a tiempo, en uniforme limpio, con sus propios suministros (a menos que se acuerde lo contrario)</li>
      <li>Honrar las cancelaciones y reprogramaciones según las reglas de la plataforma</li>
    </ul>
    <p>
      La violación puede resultar en suspensión, pérdida de ganancias pendientes y — en casos de elusión de la plataforma —
      búsqueda de daños liquidados.
    </p>

    <h2>Si algo sale mal</h2>
    <p>Tomamos los incidentes en serio. Si tu limpiador no llega, daña algo o se comporta de manera inapropiada:</p>
    <ul>
      <li><strong>Dentro de 24 horas del servicio</strong>, repórtalo a través de Contáctanos — abriremos una investigación inmediatamente.</li>
      <li><strong>Reembolsos</strong> se procesan a tu tarjeta original dentro de 5-10 días hábiles para problemas confirmados.</li>
      <li><strong>Reclamos de daños</strong> se revisan caso por caso. La mayoría de limpiadores tienen su propio seguro de responsabilidad.</li>
      <li><strong>Incidentes de seguridad</strong> son reportados a las autoridades locales cuando corresponde. El limpiador es suspendido inmediatamente durante la investigación.</li>
    </ul>

    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 not-prose my-8 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <h3 className="font-heading font-semibold text-amber-900 mb-1">¿Encontraste algo raro?</h3>
        <p className="text-sm text-amber-900/80">
          ¿Viste un perfil que no se ve bien, o tuviste una mala experiencia que quieres reportar de forma privada?
          <a href="/contact" className="underline ml-1 font-semibold inline-flex items-center gap-1">
            <MessageCircle className="w-3.5 h-3.5" /> Contacta a nuestro equipo de confianza
          </a>.
        </p>
      </div>
    </div>

    <p className="text-sm italic">
      La confianza no es un eslogan de marketing — es la base sobre la cual se sostiene cada reserva.
      Si alguna vez fallamos, dínoslo.
    </p>
  </>
);

export const PrivacyES = () => (
  <>
    <p>
      Match My Cleaner ("nosotros", "nuestro") opera un marketplace que conecta clientes con profesionales independientes de limpieza
      ("limpiadores"). Esta Política de Privacidad explica cómo recolectamos, usamos, compartimos y protegemos tu información
      personal cuando usas nuestro sitio web, experiencia móvil y servicios relacionados (colectivamente, el "Servicio").
    </p>

    <h2>1. Información que recolectamos</h2>
    <h3>1.1 Información que tú proporcionas</h3>
    <ul>
      <li><strong>Datos de cuenta</strong> — nombre, correo, número de teléfono, contraseña (encriptada), rol (cliente o limpiador).</li>
      <li><strong>Datos del cliente</strong> — dirección del servicio, código postal, preferencias de reserva, método de pago (tokenizado vía Stripe).</li>
      <li><strong>Datos del limpiador</strong> — biografía profesional, años de experiencia, certificaciones, servicios ofrecidos, tarifa por hora, horario de disponibilidad, área de servicio, y la PII requerida para verificación de antecedentes (recolectada directamente por Checkr en su formulario alojado, no por nosotros).</li>
      <li><strong>Comunicaciones</strong> — mensajes que envías vía Contáctanos, notificaciones en la app y disputas.</li>
    </ul>

    <h3>1.2 Información recolectada automáticamente</h3>
    <ul>
      <li><strong>Datos de dispositivo y registro</strong> — dirección IP, tipo de navegador, páginas visitadas, marcas de tiempo.</li>
      <li><strong>Ubicación</strong> — los limpiadores pueden compartir geolocalización en vivo para ser visibles cerca; esto es opcional vía tu navegador.</li>
      <li><strong>Cookies y almacenamiento local</strong> — para mantenerte conectado (JWT) y recordar preferencias.</li>
    </ul>

    <h3>1.3 Información de terceros</h3>
    <ul>
      <li><strong>Stripe</strong> — estado del pago y últimos 4 dígitos de tarjetas (nunca vemos números completos).</li>
      <li><strong>Checkr</strong> — resultados de verificación de antecedentes (Clear, Considerar, Suspendido).</li>
      <li><strong>Proveedores de mapas</strong> — mosaicos de OpenStreetMap para mostrar ubicaciones de limpiadores.</li>
    </ul>

    <h2>2. Cómo usamos tu información</h2>
    <ul>
      <li>Crear y administrar tu cuenta, autenticar sesiones y personalizar tu experiencia.</li>
      <li>Conectar clientes con limpiadores por ubicación, disponibilidad y precio.</li>
      <li>Procesar pagos, calcular tarifas de plataforma y desembolsar ganancias de limpiadores.</li>
      <li>Ejecutar y mostrar verificaciones de antecedentes para confianza y seguridad.</li>
      <li>Enviar notificaciones transaccionales (confirmaciones de reserva, recibos de pago, alertas de propinas).</li>
      <li>Investigar disputas, fraude y violaciones de políticas.</li>
    </ul>

    <h2>3. Cómo compartimos tu información</h2>
    <p>Nosotros <strong>no</strong> vendemos tus datos personales. Solo los compartimos con:</p>
    <ul>
      <li><strong>La otra parte de tu reserva</strong> — los clientes ven perfiles de limpiadores; los limpiadores ven el nombre del cliente y la dirección solo después de confirmar la reserva.</li>
      <li><strong>Proveedores de servicios</strong> — Stripe (pagos), Checkr (verificación), MongoDB Atlas (hosting), proveedores de correo/SMS (mensajes transaccionales).</li>
      <li><strong>Autoridades</strong> — cuando es legalmente requerido o para investigar incidentes de seguridad.</li>
    </ul>

    <h2>4. Retención de datos</h2>
    <p>
      Mantenemos los datos de tu cuenta mientras tu cuenta esté activa. Los registros de reserva, pago y disputa se retienen
      hasta 7 años por cumplimiento fiscal, antifraude y regulatorio. Puedes solicitar la eliminación de tu cuenta en cualquier
      momento vía Contáctanos — honraremos la solicitud dentro de 30 días, excepto registros que legalmente debamos conservar.
    </p>

    <h2>5. Tus derechos</h2>
    <p>Dependiendo de tu jurisdicción, puedes tener derecho a:</p>
    <ul>
      <li>Acceder a los datos personales que tenemos sobre ti.</li>
      <li>Corregir datos inexactos o incompletos.</li>
      <li>Eliminar tus datos ("derecho al olvido").</li>
      <li>Oponerte o restringir cierto procesamiento.</li>
      <li>Recibir tus datos en formato portable.</li>
      <li>Retirar consentimiento en cualquier momento.</li>
    </ul>

    <h2>6. Seguridad</h2>
    <p>
      Las contraseñas son encriptadas con bcrypt. Las sesiones usan tokens JWT firmados. Los pagos son tokenizados vía
      la infraestructura PCI-DSS Nivel 1 de Stripe. Usamos TLS en tránsito y encriptación en reposo para datos almacenados.
    </p>

    <h2>7. Menores</h2>
    <p>
      El Servicio no está destinado a menores de 18 años. No recolectamos datos de menores a sabiendas.
      Si crees que un menor nos ha proporcionado datos, contáctanos y los eliminaremos.
    </p>

    <h2>8. Transferencias internacionales</h2>
    <p>
      Nuestra infraestructura está principalmente en los EE. UU. Al usar el Servicio desde fuera de los EE. UU.,
      consientes el procesamiento de tus datos en los EE. UU.
    </p>

    <h2>9. Cambios a esta política</h2>
    <p>
      Podemos actualizar esta política periódicamente. Cuando hagamos cambios materiales, te notificaremos por correo
      y publicaremos un aviso en la plataforma.
    </p>

    <h2>10. Contáctanos</h2>
    <p>¿Preguntas sobre esta política o cómo manejamos tus datos? Contáctanos a través de la página de Contacto.</p>
  </>
);

export const TermsES = () => (
  <>
    <p>
      Estos Términos del Servicio ("Términos") forman un acuerdo vinculante entre tú y Match My Cleaner ("nosotros", "nuestro").
      Al registrarte, navegar o transaccionar en la plataforma, aceptas estos Términos en su totalidad. Si no estás de acuerdo,
      no uses el Servicio.
    </p>

    <h2>1. El marketplace, no el servicio</h2>
    <p>
      Match My Cleaner es un <strong>marketplace</strong> que conecta clientes con profesionales independientes de limpieza
      ("limpiadores"). <strong>No</strong> somos una compañía de limpieza. No empleamos limpiadores, no establecemos sus horarios,
      no supervisamos su trabajo, ni garantizamos el resultado de ningún servicio. Cada limpiador es un contratista independiente
      únicamente responsable de los servicios que provee.
    </p>

    <h2>2. Elegibilidad</h2>
    <p>Debes tener al menos 18 años y poder celebrar un contrato vinculante para usar el Servicio.</p>

    <h2>3. Cuentas</h2>
    <ul>
      <li>Eres responsable de mantener tu contraseña confidencial.</li>
      <li>Eres responsable de toda actividad bajo tu cuenta.</li>
      <li>Aceptas proporcionar información precisa, actual y completa.</li>
      <li>Podemos suspender o terminar cuentas que violen estos Términos, con o sin previo aviso.</li>
    </ul>

    <h2>4. Responsabilidades del cliente</h2>
    <ul>
      <li>Proporcionar dirección precisa, instrucciones de acceso y requisitos especiales al momento de reservar.</li>
      <li>Estar presente (o coordinar acceso) en el horario acordado.</li>
      <li>Pagar a través de la plataforma — nunca ofrezcas o aceptes pago fuera de la plataforma a tu limpiador.</li>
      <li>Tratar a los limpiadores con respeto. El acoso, discriminación o condiciones inseguras son motivo de cancelación.</li>
    </ul>

    <h2>5. Responsabilidades del limpiador</h2>
    <p>Al registrarte como limpiador, aceptaste por separado el Acuerdo de la Plataforma de Limpieza, que incluye:</p>
    <ul>
      <li>Pasar y mantener una verificación de antecedentes limpia (vía Checkr) antes de aceptar reservas.</li>
      <li>Llegar a tiempo, preparado y en condición profesional.</li>
      <li>Nunca solicitar, aceptar o transaccionar con clientes presentados por la plataforma fuera de Match My Cleaner.</li>
      <li>Honrar cancelaciones, reprogramaciones y la estructura de tarifas.</li>
      <li>Cada limpiador establece un valor por defecto de <strong>suministros</strong> al registrarse: "Traigo mis propios suministros" o "El cliente provee suministros". Los clientes pueden cambiar esto al reservar.</li>
      <li>Llevar tu propio equipo y (cuando aplique) seguro de responsabilidad.</li>
    </ul>
    <p>
      La elusión de la plataforma — incluyendo facturación directa, pagos fuera de plataforma, o compartir información personal
      para evitar el marketplace — es una violación material.
    </p>

    <h2>6. Pagos, tarifas y desembolsos</h2>
    <ul>
      <li>Al cliente se le cobra el total de la reserva al momento de la confirmación, vía Stripe.</li>
      <li>Match My Cleaner deduce una tarifa de plataforma (actualmente 15% del total). El resto se paga al limpiador.</li>
      <li>El precio puede incluir un multiplicador de demanda (surge) en horas pico y un multiplicador de nivel del limpiador (Premium 1.15× / Elite 1.25×).</li>
      <li>Los limpiadores reciben pago en un cronograma rotativo después de completarse el servicio.</li>
      <li>Las propinas pagadas a través de la plataforma van 100% al limpiador, sin tarifa de plataforma.</li>
    </ul>

    <h2>7. Cancelaciones, reprogramaciones y ausencias</h2>
    <p>Ver la <a href="/refund-policy">Política de Reembolso</a> para detalles completos.</p>
    <ul>
      <li><strong>≥ 24 horas antes del servicio</strong> — cancelación gratuita, reembolso completo.</li>
      <li><strong>&lt; 24 horas antes del servicio</strong> — aplica tarifa de cancelación tardía (actualmente 50% del total).</li>
      <li><strong>Limpiador no se presenta</strong> — reembolso completo + te ayudamos a re-reservar.</li>
    </ul>

    <h2>8. Reseñas y calificaciones</h2>
    <p>
      Solo los clientes que completaron una reserva pagada pueden enviar reseñas. Las reseñas deben ser honestas y basadas
      en experiencia de primera mano. Podemos eliminar reseñas que violen estos estándares.
    </p>

    <h2>9. Conducta prohibida</h2>
    <ul>
      <li>Publicar contenido falso, fraudulento o engañoso.</li>
      <li>Usar el Servicio para facilitar actividad ilegal.</li>
      <li>Eludir la plataforma para servicios pagados con usuarios conocidos vía Match My Cleaner.</li>
      <li>Ingeniería inversa, scraping o interferir con la operación de la plataforma.</li>
      <li>Acoso, discurso de odio, amenazas o discriminación ilegal.</li>
    </ul>

    <h2>10. Propiedad intelectual</h2>
    <p>
      El nombre Match My Cleaner, logo, diseño, código y contenido son propiedad nuestra o de nuestros licenciantes.
    </p>

    <h2>11. Renuncia de garantías</h2>
    <p>
      El Servicio se proporciona "tal cual" y "según disponibilidad", sin garantías de ningún tipo.
      El uso del Servicio es bajo tu propio riesgo.
    </p>

    <h2>12. Limitación de responsabilidad</h2>
    <p>
      En la máxima medida permitida por la ley, Match My Cleaner no es responsable por daños indirectos, incidentales, especiales,
      consecuenciales o punitivos. Nuestra responsabilidad agregada por cualquier reclamo está limitada a lo mayor entre
      (a) las tarifas de plataforma que nos pagaste en los 12 meses anteriores al reclamo, o (b) $100.
    </p>

    <h2>13. Indemnización</h2>
    <p>
      Aceptas indemnizar y mantener indemne a Match My Cleaner, sus afiliados y respectivos oficiales, directores y empleados
      de cualquier reclamo, daño, responsabilidad, costo o gasto que surja de tu uso del Servicio.
    </p>

    <h2>14. Resolución de disputas y arbitraje</h2>
    <p>
      Las disputas entre tú y Match My Cleaner se resolverán por arbitraje individual vinculante administrado por la
      American Arbitration Association bajo sus Reglas de Arbitraje del Consumidor. Renuncias al derecho a juicio por jurado
      y a participar en una acción de clase.
    </p>

    <h2>15. Ley aplicable</h2>
    <p>Estos Términos se rigen por las leyes del Estado de Delaware, EE. UU.</p>

    <h2>16. Cambios a estos Términos</h2>
    <p>
      Podemos actualizar estos Términos en cualquier momento. Los cambios materiales se publicarán con una nueva fecha
      de "Última actualización" y (cuando sea factible) se te notificarán por correo.
    </p>

    <h2>17. Contacto</h2>
    <p>¿Preguntas sobre estos Términos? Contáctanos a través de la página de Contacto.</p>
  </>
);

export const RefundES = () => (
  <>
    <p>
      Queremos que te sientas seguro cada vez que reservas en Match My Cleaner. Esta Política de Reembolso explica exactamente
      cuándo y cómo recuperas tu dinero. Aplica a todas las reservas pagadas a través de la plataforma vía Stripe.
    </p>

    <h2>1. Cancelación por el cliente</h2>
    <p>Los reembolsos dependen de cuánta anticipación tengas antes del horario del servicio:</p>
    <ul>
      <li>
        <strong>24 horas o más antes del servicio</strong> — <span className="text-emerald-700 font-semibold">Reembolso completo, sin preguntas.</span>
        Cancela en cualquier momento desde tu Panel de Cliente.
      </li>
      <li>
        <strong>Menos de 24 horas antes del servicio</strong> — Aplica una tarifa de cancelación tardía. La tarifa actual es
        <strong> 50% del total de la reserva</strong>. El 50% restante se reembolsa a tu método de pago original.
      </li>
    </ul>
    <p>
      La tarifa de cancelación tardía se divide entre tu limpiador y la plataforma (actualmente 50/50). Esto compensa a tu
      limpiador por el tiempo que reservó para ti y ya no pudo llenar.
    </p>

    <h2>2. Limpiador no se presenta o llega sin poder realizar el servicio</h2>
    <ul>
      <li><strong>Reembolso completo</strong> a tu método de pago original.</li>
      <li>Te ayudamos a re-reservar con otro limpiador — y aplicamos un crédito de $10 hacia la nueva reserva como nuestra disculpa.</li>
      <li>Reporta ausencias vía Contáctanos dentro de 48 horas.</li>
    </ul>

    <h2>3. Problemas de calidad del servicio</h2>
    <p>Si la limpieza no cumple con lo prometido:</p>
    <ul>
      <li>Contáctanos dentro de <strong>24 horas</strong> de completado el servicio con detalles e (idealmente) fotos.</li>
      <li>Revisamos con el limpiador. Las resoluciones comunes incluyen reembolso parcial (típicamente 20–50%), una re-limpieza gratuita, o reembolso completo para problemas severos.</li>
      <li>Si un limpiador recibe quejas de calidad repetidas, puede perder su nivel Premium/Elite y eventualmente ser removido de la plataforma.</li>
    </ul>

    <h2>4. Daños o pérdidas durante el servicio</h2>
    <ul>
      <li>Documenta el daño con fotos y descripción escrita inmediatamente.</li>
      <li>Contáctanos dentro de <strong>72 horas</strong> del servicio.</li>
      <li>La mayoría de limpiadores en la plataforma tienen su propio seguro de responsabilidad — te ayudamos a coordinar un reclamo.</li>
    </ul>

    <h2>5. Propinas</h2>
    <p>
      Las propinas pagadas a través de la plataforma después de una reserva completada son <strong>no reembolsables</strong>,
      ya que van directamente a tu limpiador sin tarifa de plataforma.
    </p>

    <h2>6. Reservas recurrentes / suscripción</h2>
    <ul>
      <li>Puedes cancelar cualquier instancia futura de una reserva recurrente sin cargo si cancelas ≥ 24 horas antes de esa instancia.</li>
      <li>Las cancelaciones tardías en cualquier instancia individual siguen la regla del 50%.</li>
      <li>Cancelar la serie recurrente solo afecta instancias futuras — las limpiezas completadas no se reembolsan.</li>
    </ul>

    <h2>7. Pagos disputados / contracargos</h2>
    <p>
      Por favor contáctanos primero antes de iniciar un contracargo con tu banco. La mayoría de problemas se resuelven
      dentro de 48 horas. Los contracargos eluden nuestro proceso de resolución y pueden resultar en suspensión temporal
      de tu cuenta pendiente de investigación.
    </p>

    <h2>8. Cómo se procesan los reembolsos</h2>
    <ul>
      <li>Los reembolsos se emiten a tu <strong>método de pago original</strong> vía Stripe.</li>
      <li>La mayoría de reembolsos aparecen en tu estado de cuenta dentro de <strong>5–10 días hábiles</strong>.</li>
      <li>Recibirás una notificación en la app y confirmación por correo cuando el reembolso se inicie.</li>
    </ul>

    <h2>9. Qué no es reembolsable</h2>
    <ul>
      <li>Servicios donde no estuviste presente y no proporcionaste acceso alternativo (cuenta como ausencia tuya).</li>
      <li>Quejas enviadas más de 7 días después del servicio.</li>
      <li>Preferencias subjetivas no especificadas al momento de reservar.</li>
    </ul>

    <h2>10. Preguntas o apelaciones</h2>
    <p>
      Si no estás de acuerdo con una decisión de reembolso, puedes apelar respondiendo al correo de resolución con contexto
      adicional. Las apelaciones son revisadas por un miembro separado del equipo de confianza dentro de 5 días hábiles.
    </p>
  </>
);
